import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Row, Col, Switch, Tooltip } from 'antd';
import {
    ArrowLeftOutlined,
} from '@ant-design/icons';
import ImageZoom from './ImageZoom';
import './AIEditorModalViewer.scss';
import './ImageZoom.scss';
import './HighResModal.scss';
import ENVIRONMENT from '../../../../environments';

const CompareSiloView = ({ isOpen, onClose, imageData, closeAllModals }) => {
    const [darkMode, setDarkMode] = useState(false);
    const [compareImageType, setCompareImageType] = useState('hero_0');
    const [aiImageResolution, setAiImageResolution] = useState(null);
    const [siloImageResolution, setSiloImageResolution] = useState(null);
    const [isLoadingAiResolution, setIsLoadingAiResolution] = useState(false);
    const [isLoadingSiloResolution, setIsLoadingSiloResolution] = useState(false);
    const [syncZoomPan, setSyncZoomPan] = useState(false);
    const imageZoomRef = useRef(null);
    const siloImageZoomRef = useRef(null);
    const isSyncingRef = useRef(false);
    const aiTransformRef = useRef({ scale: 1, x: 0, y: 0 });
    const siloTransformRef = useRef({ scale: 1, x: 0, y: 0 });
    const aiImageDimensionsRef = useRef({ width: 0, height: 0 });
    const siloImageDimensionsRef = useRef({ width: 0, height: 0 });

    
    // Synchronization handlers with combined transform
    const handleAiImageTransformChange = useCallback(({ scale, positionX, positionY }) => {
        aiTransformRef.current = { scale, x: positionX, y: positionY };
        if (!syncZoomPan || isSyncingRef.current) return;
        
        isSyncingRef.current = true;
        if (siloImageZoomRef.current && 
            aiImageDimensionsRef.current.width > 0 && 
            siloImageDimensionsRef.current.width > 0) {
            
            // Calculate relative position as percentage of image dimensions (accounting for scale)
            const relativeX = positionX / aiImageDimensionsRef.current.width;
            const relativeY = positionY / aiImageDimensionsRef.current.height;
            
            // Apply to silo image with its dimensions
            const newX = relativeX * siloImageDimensionsRef.current.width;
            const newY = relativeY * siloImageDimensionsRef.current.height;
            
            siloTransformRef.current = { scale, x: newX, y: newY };
            siloImageZoomRef.current.setTransform(scale, newX, newY, 0);
        }
        setTimeout(() => {
            isSyncingRef.current = false;
        }, 50);
    }, [syncZoomPan]);

    const handleSiloImageTransformChange = useCallback(({ scale, positionX, positionY }) => {
        siloTransformRef.current = { scale, x: positionX, y: positionY };
        if (!syncZoomPan || isSyncingRef.current) return;
        
        isSyncingRef.current = true;
        if (imageZoomRef.current && 
            aiImageDimensionsRef.current.width > 0 && 
            siloImageDimensionsRef.current.width > 0) {
            
            // Calculate relative position as percentage of image dimensions (accounting for scale)
            const relativeX = positionX / siloImageDimensionsRef.current.width;
            const relativeY = positionY / siloImageDimensionsRef.current.height;
            
            // Apply to AI image with its dimensions
            const newX = relativeX * aiImageDimensionsRef.current.width;
            const newY = relativeY * aiImageDimensionsRef.current.height;
            
            aiTransformRef.current = { scale, x: newX, y: newY };
            imageZoomRef.current.setTransform(scale, newX, newY, 0);
        }
        setTimeout(() => {
            isSyncingRef.current = false;
        }, 50);
    }, [syncZoomPan]);
    
    // Helper function to get the current render based on renderIndex
    const getCurrentRender = () => {
        if (imageData?.renders && imageData?.renderIndex !== undefined) {
            return imageData.renders[imageData.renderIndex];
        }
        return null;
    };

    // Helper function to get inspiration_image_uri from the current render
    const getInspirationImageUri = () => {
        const currentRender = getCurrentRender();
        return currentRender?.inspiration_image_uri || imageData?.inspiration_image_uri;
    };

    // Helper function to get render_props from the current render (excluding product props)
    const getRenderProps = () => {
        const currentRender = getCurrentRender();
        const renderProps = currentRender?.render_props || imageData?.render_props;
        if (!renderProps) return [];
        return renderProps.filter(prop => !prop.product);
    };

    // All images from the hero product (product: true), preserving primary first
    // Returns array of { image_uri, rgba_path }
    const getHeroProductImages = () => {
        const currentRender = getCurrentRender();
        const renderProps = currentRender?.render_props || imageData?.render_props;
        if (!renderProps?.length) return [];
        const hero = renderProps.find(p => p.product === true);
        if (!hero) return [];
        if (hero.images?.length) {
            const primary = hero.images.filter(img => img.primary_image);
            const rest = hero.images.filter(img => !img.primary_image);
            return [...primary, ...rest]
                .filter(img => img.image_uri)
                .map(img => ({ image_uri: img.image_uri, rgba_path: img.rgba_path || null }));
        }
        return hero.image_uri ? [{ image_uri: hero.image_uri, rgba_path: hero.rgba_path || null }] : [];
    };

    // Flat list of all images across non-product props
    // Returns array of { image_uri, rgba_path }
    const getAllPropImages = () => {
        const nonProductProps = getRenderProps();
        const result = [];
        nonProductProps.forEach(prop => {
            if (prop.images?.length) {
                prop.images.forEach(img => {
                    if (img.image_uri) result.push({ image_uri: img.image_uri, rgba_path: img.rgba_path || null });
                });
            } else if (prop.image_uri) {
                result.push({ image_uri: prop.image_uri, rgba_path: prop.rgba_path || null });
            }
        });
        return result;
    };

    const closeModal = () => {
        closeAllModals();
    };
    const goBack = () => {
        onClose();
    };

    const extractImageResolution = (imageUrl, setResolution, setIsLoading, fallbackUrl) => {
        if (!imageUrl) return;

        setIsLoading(true);
        const img = new Image();

        img.onload = () => {
            setResolution(`${img.naturalWidth} × ${img.naturalHeight}`);
            setIsLoading(false);
        };

        img.onerror = () => {
            if (fallbackUrl && img.src !== fallbackUrl) {
                img.src = fallbackUrl;
            } else {
                setResolution('Resolution not available');
                setIsLoading(false);
            }
        };

        img.src = imageUrl;
    };

    const getAiImageResolution = () => {
        if (aiImageResolution) {
            return aiImageResolution;
        }
        if (isLoadingAiResolution) {
            return '';
        }
        return '';
    };

    const getImageResolution = () => {
        if (siloImageResolution) {
            return siloImageResolution;
        }
        if (isLoadingSiloResolution) {
            return '';
        }
        return '';
    };

    // Reset compare type when modal opens with new imageData
    useEffect(() => {
        if (isOpen && imageData) {
            if (getHeroProductImages().length > 0) {
                setCompareImageType('hero_0');
            } else if (getInspirationImageUri()) {
                setCompareImageType('inspiration');
            } else if (getAllPropImages().length > 0) {
                setCompareImageType('prop_img_0');
            }
            setDarkMode(false);
        }
    }, [isOpen, imageData]);

    useEffect(() => {
        if (!isOpen) return;
        if (imageData?.selectedImageUrl) {
            extractImageResolution(getCompressedViewUrl(imageData.selectedImageUrl), setAiImageResolution, setIsLoadingAiResolution, imageData.selectedImageUrl);
        }
        if (getSiloImage()) {
            extractImageResolution(getSiloImage(true), setSiloImageResolution, setIsLoadingSiloResolution, getSiloImage());
        }
    }, [isOpen, imageData?.selectedImageUrl, imageData?.renderIndex, imageData?.renders, compareImageType, darkMode]);


    const getSiloImage = (compressed = false) => {
        const inspirationUri = getInspirationImageUri();

        if (compareImageType === 'inspiration' && inspirationUri) {
            return inspirationUri.includes('https://')
                ? inspirationUri
                : ENVIRONMENT.getBaseURL(imageData?.platform) + inspirationUri;
        }

        if (compareImageType === 'input_image' && imageData?.original_input_image_uri) {
            const url = imageData.original_input_image_uri.includes('https://')
                ? imageData.original_input_image_uri
                : ENVIRONMENT.getBaseURL(imageData?.platform) + imageData.original_input_image_uri;
            return compressed ? getCompressedViewUrl(url) : url;
        }

        if (compareImageType?.startsWith('hero_')) {
            const index = parseInt(compareImageType.split('_')[1], 10);
            const heroImages = getHeroProductImages();
            const imgObj = heroImages[index];
            if (imgObj) {
                const uri = imgObj.rgba_path || imgObj.image_uri;
                return uri.includes('https://')
                    ? uri
                    : ENVIRONMENT.getBaseURL(imageData?.platform) + uri;
            }
        }

        if (compareImageType?.startsWith('prop_img_')) {
            const index = parseInt(compareImageType.split('_')[2], 10);
            const propImages = getAllPropImages();
            const imgObj = propImages[index];
            if (imgObj) {
                const uri = imgObj.rgba_path || imgObj.image_uri;
                return uri.includes('https://')
                    ? uri
                    : ENVIRONMENT.getBaseURL(imageData?.platform) + uri;
            }
        }

        return imageData?.src;
    };

    const hasInspirationImage = () => {
        return !!getInspirationImageUri();
    };

    const getImageUrl = (uri, isFullUrl = false) => {
        if (!uri) return '';
        if (uri.includes('https://') || isFullUrl) {
            return uri;
        }
        return ENVIRONMENT.getBaseURL(imageData?.platform) + uri;
    };

    const getInspirationThumbnail = () => {
        const inspirationUri = getInspirationImageUri();
        if (inspirationUri) {
            return getImageUrl(inspirationUri);
        }
        return '';
    };

    const getInputImageThumbnail = () => {
        if (imageData?.original_input_image_uri) {
            return getImageUrl(imageData.original_input_image_uri);
        }
        return '';
    };

    const getCompressedViewUrl = (url) => {
        if (!url) return url;
        return url.replace(/\.[^./?#]+(\?|$)/, '_compressed.webp$1');
    };

    const handleImageCardClick = (type) => {
        setCompareImageType(type);
    };

    return (
        <Modal
            className='ai-image-viewer-modal compare-modal'
            visible={isOpen}
            onCancel={goBack}
            title={<span className="manrope f-14 lh-22 w-500 black-085 justify-space-between" style={{ width: '58%' }}>
                <span className='cursor-pointer' onClick={goBack}><ArrowLeftOutlined /> <span className='ml-6'>Back</span></span>
                <span className="manrope f-20 lh-28 w-500 black-085">Comparison View</span>
            </span>}
            closable={true}
            footer={[

            ]}
            centered={true}>
            <Row>
                <Col span={24} className='justify-space-between'>
                    <Col span={12} className='ai-img-box'>
                        <div className='justify-space-between'>
                            <div className={`manrope f-14 black-085 ai-comparison-img-tag left`}>AI Image</div>
                            {compareImageType === 'input_image' && (
                                <div className={`button-container-compare bg-gray`}>
                                    <span className={`manrope f-12 w-500 lh-20 black-085`}>Sync zoom/pan</span>
                                    <Switch
                                        className='silo-compare-switch'
                                        checked={syncZoomPan}
                                        onChange={(checked) => {
                                            setSyncZoomPan(checked);
                                        }} />
                                </div>
                            )}
                        </div>
                        <div className='compare-ai-image-container'>
                            <ImageZoom
                                ref={imageZoomRef}
                                imageSrc={getCompressedViewUrl(imageData?.selectedImageUrl)}
                                fallbackSrc={imageData?.selectedImageUrl}
                                className="compare-ai-image"
                                onTransformChange={handleAiImageTransformChange}
                                onImageLoad={(dimensions) => {
                                    aiImageDimensionsRef.current = dimensions;
                                }}
                            />
                            <div style={{ position: 'absolute', bottom: '24px', left: '24px' }}>
                                <div className='image-resolution-display'>
                                    <span className='manrope f-14 w-500 white'>{getAiImageResolution()}</span>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col span={12} className='ai-img-box'>
                        <div className='justify-space-between'>
                            <div className={`ai-comparison-img-tag left`}>
                                <span className='manrope f-14 black-085'>
                                    {compareImageType === 'inspiration' ? (imageData?.generation_type == 'product_variant' ? 'Material Reference' : 'Inspiration Image')
                                        : compareImageType === 'input_image' ? 'Input Image'
                                        : compareImageType?.startsWith('hero_') ? (
                                            getHeroProductImages().length === 1
                                                ? 'Product Reference'
                                                : parseInt(compareImageType.split('_')[1], 10) === 0
                                                    ? 'Primary Product Angle'
                                                    : `Product Angle ${parseInt(compareImageType.split('_')[1], 10) + 1}`
                                        )
                                        : compareImageType?.startsWith('prop_img_') ? `Prop Image ${parseInt(compareImageType.split('_')[2], 10) + 1}`
                                        : ''}
                                </span>
                            </div>

                            {(() => {
                                const hasDarkMode = compareImageType?.startsWith('hero_') || compareImageType?.startsWith('prop_img_');
                                if (!hasDarkMode) return null;
                                return (
                                    <div className={`button-container-compare bg-gray`}>
                                        <span className={`manrope f-12 w-500 lh-20 black-085`}>Dark mode</span>
                                        <Switch
                                            className='silo-compare-switch'
                                            checked={darkMode}
                                            onChange={(checked) => {
                                                setDarkMode(checked);
                                            }} />
                                    </div>
                                );
                            })()}
                        </div>

                        <div className='compare-ai-image-container'>
                            <ImageZoom
                                ref={siloImageZoomRef}
                                imageSrc={getSiloImage(true)}
                                fallbackSrc={getSiloImage()}
                                className={`compare-ai-image ${darkMode ? `dark-mode-bg` : ''}`}
                                onTransformChange={handleSiloImageTransformChange}
                                onImageLoad={(dimensions) => {
                                    siloImageDimensionsRef.current = dimensions;
                                }}
                            />
                            <div style={{ position: 'absolute', bottom: '24px', left: '24px' }}>
                                {compareImageType?.startsWith('hero_') && (() => {
                                    const currentRender = getCurrentRender();
                                    const renderProps = currentRender?.render_props || imageData?.render_props;
                                    const hero = renderProps?.find(p => p.product === true);
                                    return hero?.width != null && hero?.height != null && hero?.depth != null ? (
                                        <div className={`ai-comparison-img-tag dark-bg`} style={{ marginBottom: 8 }}>
                                            <span className={`manrope f-14 lh-20 w-500 white`}>
                                                {hero.width}"W x {hero.height}"H x {hero.depth}"D
                                            </span>
                                        </div>
                                    ) : null;
                                })()}
                                <div className={`ai-comparison-img-tag dark-bg`}>
                                    <span className={`manrope f-14 lh-20 w-500 white`}>{getImageResolution()}</span>
                                </div>
                            </div>

                            {(hasInspirationImage() || getHeroProductImages().length > 0 || getAllPropImages().length > 0 || ((imageData?.is_3d_scene_image || imageData?.is_edit_request || imageData?.request_type == 'edit_request' || imageData?.request_type == '4k_request') && imageData?.original_input_image_uri)) && (
                                <div className='compare-card-pos'>
                                    {/* Hero product angles */}
                                    {getHeroProductImages().map((imgObj, idx) => {
                                        const type = `hero_${idx}`;
                                        const heroCount = getHeroProductImages().length;
                                        const label = heroCount === 1
                                            ? 'Product Reference'
                                            : idx === 0 ? 'Primary Product Angle' : `Product Angle ${idx + 1}`;
                                        const imgSrc = getImageUrl(imgObj.rgba_path || imgObj.image_uri);
                                        return (
                                            <Tooltip key={type} title={<span className='manrope f-12 white'>{label}</span>}>
                                                <div
                                                    onClick={() => handleImageCardClick(type)}
                                                    className={`compare-card-img-style ${compareImageType === type ? 'selected-img' : ''}`}
                                                    onMouseEnter={(e) => { if (compareImageType !== type) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.borderColor = '#D9D9D9'; } }}
                                                    onMouseLeave={(e) => { if (compareImageType !== type) { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.borderColor = '#d9d9d9'; } }}
                                                >
                                                    <img src={imgSrc} alt={label} className='img-setting' />
                                                </div>
                                            </Tooltip>
                                        );
                                    })}

                                    {/* Inspiration image */}
                                    {hasInspirationImage() && (
                                        <Tooltip title={<span className='manrope f-12 white'>{imageData?.generation_type == 'product_variant' ? 'Material Reference' : 'Inspiration Image'}</span>}>
                                            <div
                                                onClick={() => handleImageCardClick('inspiration')}
                                                className={`compare-card-img-style ${compareImageType === 'inspiration' ? 'selected-img' : ''}`}
                                                onMouseEnter={(e) => { if (compareImageType !== 'inspiration') { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.borderColor = '#D9D9D9'; } }}
                                                onMouseLeave={(e) => { if (compareImageType !== 'inspiration') { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.borderColor = '#d9d9d9'; } }}
                                            >
                                                <img src={getInspirationThumbnail()} alt="Inspiration Image" className='img-setting' />
                                            </div>
                                        </Tooltip>
                                    )}

                                    {/* Input image (edit/4k requests) */}
                                    {(imageData?.is_3d_scene_image || imageData?.is_edit_request || imageData?.request_type == 'edit_request' || imageData?.request_type == '4k_request') && imageData?.original_input_image_uri && (
                                        <Tooltip title={<span className='manrope f-12 white'>Input Image</span>}>
                                            <div
                                                onClick={() => handleImageCardClick('input_image')}
                                                className={`compare-card-img-style ${compareImageType === 'input_image' ? 'selected-img' : ''}`}
                                                onMouseEnter={(e) => { if (compareImageType !== 'input_image') { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.borderColor = '#D9D9D9'; } }}
                                                onMouseLeave={(e) => { if (compareImageType !== 'input_image') { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.borderColor = '#d9d9d9'; } }}
                                            >
                                                <img
                                                    src={getCompressedViewUrl(getInputImageThumbnail())}
                                                    onError={(e) => { if (e.target.src !== getInputImageThumbnail()) e.target.src = getInputImageThumbnail(); }}
                                                    alt="Input Image"
                                                    className='img-setting'
                                                />
                                            </div>
                                        </Tooltip>
                                    )}

                                    {/* Non-product prop images (flat) */}
                                    {getAllPropImages().map((imgObj, idx) => {
                                        const type = `prop_img_${idx}`;
                                        const label = `Prop Image ${idx + 1}`;
                                        const imgSrc = getImageUrl(imgObj.image_uri);
                                        return (
                                            <Tooltip key={type} title={<span className='manrope f-12 white'>{label}</span>}>
                                                <div
                                                    onClick={() => handleImageCardClick(type)}
                                                    className={`compare-card-img-style ${compareImageType === type ? 'selected-img' : ''}`}
                                                    onMouseEnter={(e) => { if (compareImageType !== type) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.borderColor = '#D9D9D9'; } }}
                                                    onMouseLeave={(e) => { if (compareImageType !== type) { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.borderColor = '#d9d9d9'; } }}
                                                >
                                                    <img src={imgSrc} alt={label} className='img-setting' />
                                                </div>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </Col>
                </Col>
            </Row>

        </Modal>
    );
};

export default CompareSiloView;
